import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { Theme } from "../../server/db/schema";
import { api, inFrame } from "./api";
import type { Me } from "./queries";

export type { Theme };

const STORAGE_KEY = "theme";
const media = () => globalThis.matchMedia("(prefers-color-scheme: dark)");

/**
 * Inside ServiceM8's web app (shown in a frame) the app is always light, to match ServiceM8, which
 * has no dark mode. The person's own choice still applies when they open the app directly, and in
 * ServiceM8's phone app (not a frame).
 */
export const themeLocked = inFrame;

/** Toggle the `dark` class on <html>. Also cached locally so index.html can apply it before React loads. */
function applyTheme(theme: Theme) {
    const dark = !themeLocked && (theme === "dark" || (theme === "system" && media().matches));
    document.documentElement.classList.toggle("dark", dark);
    document.documentElement.style.colorScheme = dark ? "dark" : "light";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#1b1c1e" : "#ffffff");
    try {
        localStorage.setItem(STORAGE_KEY, theme);
    } catch {
        // Storage can be unavailable (private mode); the theme still applies for this page.
    }
}

/**
 * Change the signed-in user's theme: applied instantly, saved to their account, and pushed by
 * the server to their other devices. Used by the account menu and Settings.
 */
export function useSetTheme() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (theme: Theme) => api("/me/preferences", { method: "PATCH", body: { theme } }),
        onMutate: (theme) => {
            applyTheme(theme);
            qc.setQueryData<Me | null>(["me"], (m) => (m ? { ...m, user: { ...m.user, theme } } : m));
        },
        onError: () => qc.invalidateQueries({ queryKey: ["me"] }),
    });
}

/** Keep the page in step with the user's saved theme, and with the OS when it's "system". */
export function useTheme(theme: Theme | undefined) {
    useEffect(() => {
        if (!theme) return;
        applyTheme(theme);
        if (theme !== "system") return;
        const mq = media();
        const onChange = () => applyTheme("system");
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, [theme]);
}
